/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isEmpty, isNone } from '@ember/utils';

import { getPayloadValue, PAYLOAD_PATHS } from 'cio/utils/push/custom-payload';
import PushService from 'cio/services/push';
import { tracked } from '@glimmer/tracking';
import TemplateModel from 'cio/models/template';
import { CORRESPONDING_DEVICE_OPTIONS, Platform } from 'cio/utils/push/platforms';

interface PreviewerArgs {
  isRefreshing: boolean;
  message: Record<string, any>;
  preview: Record<string, any>;
  showTabs: boolean; // = true;
  isCustomPayload: boolean;
  platform: Platform;
  action: TemplateModel['action'];
  newsletterContent: boolean;
  initialDeviceOption: string;
  overrideDevicePreview?: Record<string, any>;
}

export type DevicePreview = {
  label: string;
  platform: Platform;
  value: string;
  default?: boolean;
  type?: string;
  maxLines?: number;
};

const PREVIEW_KEYS: (keyof typeof PAYLOAD_PATHS)[] = ['body', 'title', 'image', 'link'];
const PLATFORMS: Platform[] = ['android', 'ios'];

export const DEVICE_PREVIEWS: DevicePreview[] = [
  {
    label: 'iOS Banner',
    platform: 'ios',
    type: 'banner',
    default: false,
    value: 'ios_banner',
    maxLines: 2,
  },
  {
    label: 'iOS Expanded',
    platform: 'ios',
    type: 'expanded',
    default: false,
    value: 'ios_expanded',
    maxLines: 0,
  },
  {
    label: 'iOS Lock Screen',
    platform: 'ios',
    type: 'lock-screen',
    default: true,
    value: 'ios_lock_screen',
    maxLines: 4,
  },
  {
    label: 'Android Heads-Up',
    platform: 'android',
    type: 'heads-up',
    default: false,
    value: 'android_heads_up',
    maxLines: 1,
  },
  {
    label: 'Android Expanded',
    platform: 'android',
    type: 'expanded',
    default: false,
    value: 'android_expanded',
    maxLines: 0,
  },
  {
    label: 'Android Lock Screen/Collapsed',
    platform: 'android',
    type: 'lock-screen',
    default: true,
    value: 'android_lock_screen',
    maxLines: 1,
  },
];

export const SIMPLE_DEVICE_PREVIEWS: DevicePreview[] = [
  {
    label: 'Android',
    value: 'android',
    platform: 'android',
  },
  {
    label: 'iOS',
    value: 'ios',
    platform: 'ios',
  },
];

export default class Previewer extends Component<PreviewerArgs> {
  @service push!: PushService;

  @tracked currentPreviewTab = 'device';
  @tracked currentDeviceOption?: string;
  @tracked hasCustomPayload = false;
  @tracked parsedBodyJson: Record<string, any> = {};
  @tracked bodyMeta: Record<string, any> = {};

  constructor(owner: unknown, args: PreviewerArgs) {
    super(owner, args);

    if (args.initialDeviceOption) {
      this.currentDeviceOption = args.initialDeviceOption;
    }
  }

  get showTabs() {
    return this.args.showTabs ?? true;
  }

  get messageHasPreviewKeys() {
    let translatedBodyJson = this.translatedBodyJson ?? {};

    return PLATFORMS.some((platform) => {
      let platformMessage = translatedBodyJson[platform] ?? {};
      return PREVIEW_KEYS.some((key) => !isEmpty(platformMessage[key]));
    });
  }

  get currentDevicePreview() {
    return (
      this.args.overrideDevicePreview ?? this.deviceOptions.find((option) => option.value === this.currentDeviceOption)
    );
  }

  @computed(
    'args.preview',
    'isCustomPayload',
    'args.platform',
    'bodyMeta',
    'push.platformNames',
    'args.action.{isPlatformSelected,sendToPlatform}',
  )
  get deviceOptions(): DevicePreview[] {
    let options = [...DEVICE_PREVIEWS];

    if (this.args.preview) {
      let enabledPlatforms = this.push.platformNames || [];

      if (this.args.action?.isPlatformSelected) {
        // if the action is targeting a specific platform, only show those
        // options
        options = options.filterBy('platform', this.args.action.sendToPlatform as Platform);
      } else if (this.isCustomPayload) {
        // if we're in the custom payload editor, only show options for the
        // currently selected platform editor
        options = options.filterBy('platform', this.args.platform);
      } else if (!isEmpty(enabledPlatforms)) {
        // if we're in the simple push editor, only show options for
        // platforms that are enabled (if there are any, otherwise we show
        // all)
        options = options.filter((option) => enabledPlatforms.includes(option.platform));
      }
    } else if (this.args.newsletterContent) {
      return options;
    } else {
      // for delivery details, show a simple iOS/Android dropdown
      return SIMPLE_DEVICE_PREVIEWS;
    }

    return options;
  }

  get isCustomPayload() {
    // In preview mode (used in the composer), use isCustomPayload to check
    // which mode we're in.
    // Otherwise, infer mode (simple/custom) from whether parsing bodyJson was
    // successful
    if (this.args.preview) {
      return this.args.isCustomPayload;
    }

    return this.hasCustomPayload;
  }

  get hasMultiplePlatformBodies() {
    if (!this.isCustomPayload) {
      return false;
    }

    let jsonHasAllPlatformValues = PLATFORMS.every((key) => !this.bodyMeta[key]?.empty);
    return jsonHasAllPlatformValues;
  }

  get message() {
    let message = this.isCustomPayload ? this.messageFromBodyJson : this.args.message;
    return { ...message };
  }

  get translatedBodyJson() {
    let translatedBodyJson: Record<string, any> = {};

    if (isNone(this.parsedBodyJson)) {
      return null;
    }

    PLATFORMS.forEach((platform) => {
      let message: Record<string, any> | null = {};

      let platformBody = this.parsedBodyJson[platform];

      if (isNone(platformBody)) {
        message = null;
      } else {
        PREVIEW_KEYS.forEach((key) => {
          message![key] = getPayloadValue(platformBody, platform, key);
        });
      }

      translatedBodyJson[platform] = message;
    });

    return translatedBodyJson;
  }

  get messageFromBodyJson() {
    if (isNone(this.translatedBodyJson) || isNone(this.currentDevicePreview)) {
      return null;
    }

    let platformBody = this.translatedBodyJson[this.currentDevicePreview.platform];

    if (isNone(platformBody)) {
      return null;
    }

    return platformBody;
  }

  get tabs() {
    if (this.args.preview) {
      return [
        { label: 'Device', value: 'device' },
        { label: 'JSON', value: 'json' },
      ];
    }

    if (this.isCustomPayload) {
      if (this.messageHasPreviewKeys) {
        return [
          { label: 'Message', value: 'message' },
          { label: 'JSON', value: 'json' },
        ];
      }

      return [{ label: 'JSON', value: 'json' }];
    }

    return [{ label: 'Message', value: 'message' }];
  }

  @action
  didReceiveAttrs() {
    if (!this.args.isRefreshing) {
      let bodyJson = this.args.message?.body ?? '';

      if (typeof bodyJson === 'string') {
        try {
          bodyJson = JSON.parse(bodyJson || '{}');

          let parsedBodyJson: Record<string, any> = {};
          let bodyMeta: Record<string, any> = {};

          PLATFORMS.forEach((key) => {
            let platformBody = bodyJson[key];
            let platformJsonValid = true;
            let platformEmpty = true;

            // Platform-specific custom payloads used to be strings, so we
            // JSON.parsed them.
            // Now the push preview endpoint returns these as objects already,
            // so the parse isn't needed anymore.
            // However, we'll check for whether the body is a string and still
            // do the parsing, just in case we revert back to strings there at
            // some point.
            if (typeof platformBody === 'string') {
              try {
                platformBody = JSON.parse(platformBody);
              } catch (e) {
                platformJsonValid = false;
              }
            }

            if (!isNone(platformBody) && Object.keys(platformBody).length > 0) {
              parsedBodyJson[key] = platformBody;
              platformEmpty = false;
            }

            bodyMeta[key] = {
              valid: platformJsonValid,
              empty: platformEmpty,
            };
          });

          this.parsedBodyJson = parsedBodyJson;
          this.bodyMeta = bodyMeta;
          this.hasCustomPayload = true;
        } catch (e) {
          let bodyMeta: Record<string, any> = {};
          PLATFORMS.forEach((key) => {
            bodyMeta[key] = {
              valid: false,
            };
          });

          // if parsing fails, it means that the provided bodyJson is either just
          // a string, so this push is a "simple push", with bodyJson being the
          // body content, or it's a custom payload with an invalid JSON.
          //
          // The "simple push" can happen in deliveries, where body can either
          // be the rendered body of a simple push (string), or the stringified
          // JSON of a custom payload. In deliveries, the body is passed in as
          // bodyJson for the purpose of determining whether we're dealing with
          // a custom payload.
          //
          // The invalid JSON custom payload scenario can happen when either
          // platform's custom payload is invalid. In this case the whole
          // bodyJson string parsing will fail (as the push preview endpoint
          // returns objects, and not strings, under the platform-specific body
          // keys). To handle this case, we set the bodyMetas to invalid.
          this.bodyMeta = bodyMeta;
          this.hasCustomPayload = false;
        }
      }
    }

    if (!this.showTabs) {
      this.currentPreviewTab = 'device';
    } else if (!this.args.preview) {
      this.currentPreviewTab = this.tabs[0].value;
    }

    // when in preview and the device dropdown has no value or the dropdown
    // values have changed (for example when switching between custom payload
    // editor tabs), change the selected option to the default option (if it
    // exists)
    if (
      (this.args.preview || this.args.newsletterContent) &&
      (isNone(this.currentDeviceOption) ||
        !this.deviceOptions.some((option) => option.value === this.currentDeviceOption))
    ) {
      let deviceOption = this.deviceOptions.find((option) => option.default);
      if (!isNone(deviceOption)) {
        this.currentDeviceOption = deviceOption.value;
      }
    } else if (!this.args.preview && isNone(this.currentDeviceOption)) {
      // if we're in delivery details and in custom payload mode, select the
      // dropdown option for the platform that has a valid and non-empty entry
      // in bodyJson
      let bodyMeta = this.bodyMeta || {};
      let firstValidPlatform = Object.keys(bodyMeta).find((key) => {
        let platformMeta = bodyMeta[key];
        return !platformMeta.empty && platformMeta.valid;
      });

      // if no valid and non-empty entry has been found, use the first
      // configured platform
      if (isNone(firstValidPlatform)) {
        firstValidPlatform = this.push.platformNames.firstObject;
      }

      this.currentDeviceOption = firstValidPlatform;
    }
  }

  @action
  didChangePushPreview() {
    if (!this.args.action) {
      return;
    }

    // To be used for an event in the future
  }

  @action
  onPlatformChange(platform: string) {
    if (this.deviceOptions === SIMPLE_DEVICE_PREVIEWS) {
      // delivery details
      this.currentDeviceOption = SIMPLE_DEVICE_PREVIEWS.find(
        (devicePreview) => devicePreview.platform === platform,
      )?.value;
      return;
    }

    if (this.currentDeviceOption) {
      this.currentDeviceOption = CORRESPONDING_DEVICE_OPTIONS[this.currentDeviceOption];
    }
  }
}
